import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';

export interface ISha256 {
  hash(input: Uint8Array | string): string;
  hmac(secret: Uint8Array | string, message: string): string;
  hmacBytes(secret: Uint8Array | string, message: string): Uint8Array;
}

/**
 * `@noble/hashes` is audited, dependency-free, and evaluates identically under Node, browsers, and every
 * bundler target (unlike `js-sha256`, which auto-detects Node at module-eval time and unconditionally does
 * `require('buffer').Buffer.from(...)` — this crashed Next.js static-page generation, whose sandboxed
 * `require` stubs Node core modules while still reporting as Node).
 */
export const Sha256: ISha256 = {
  /**
   * Computes the SHA-256 hex digest of the given input.
   * @param input - The string or raw bytes to hash.
   * @returns The lowercase hex SHA-256 digest.
   */
  hash(input: Uint8Array | string): string {
    return bytesToHex(sha256(typeof input === 'string' ? utf8ToBytes(input) : input));
  },
  /**
   * Computes the HMAC-SHA256 hex digest of the given message.
   * @param secret - The secret key; a string is read as UTF-8.
   * @param message - The message to sign.
   * @returns The lowercase hex HMAC-SHA256 digest.
   */
  hmac(secret: Uint8Array | string, message: string): string {
    return bytesToHex(Sha256.hmacBytes(secret, message));
  },
  /**
   * Computes the HMAC-SHA256 of the given message as raw bytes. Needed by key-derivation chains that feed
   * one HMAC's raw output in as the next one's key — notably AWS SigV4, where re-hexing each intermediate
   * would produce a different (wrong) signing key.
   * @param secret - The secret key; a string is read as UTF-8.
   * @param message - The message to sign.
   * @returns The 32-byte HMAC-SHA256 digest.
   */
  hmacBytes(secret: Uint8Array | string, message: string): Uint8Array {
    return hmac(sha256, typeof secret === 'string' ? utf8ToBytes(secret) : secret, utf8ToBytes(message));
  }
};
